import { useState, useEffect, useRef, useCallback } from 'react';

interface IdleTimerProps {
    onIdle: () => void;
    countdownTimeInSeconds?: number;
    isPwaInstalled?: boolean;
}

const IDLE_TIME_BROWSER_SECONDS = 300; // 5 minutes
const IDLE_TIME_PWA_SECONDS = 900; // 15 minutes

export const useIdleTimer = ({
    onIdle,
    countdownTimeInSeconds = 60, // 1 minute
    isPwaInstalled = false,
}: IdleTimerProps) => {
    const [isIdle, setIsIdle] = useState(false);
    const [countdown, setCountdown] = useState(countdownTimeInSeconds);

    const idleTimeoutRef = useRef<number | null>(null);
    const countdownIntervalRef = useRef<number | null>(null);

    const effectiveIdleTimeInSeconds = isPwaInstalled ? IDLE_TIME_PWA_SECONDS : IDLE_TIME_BROWSER_SECONDS;

    const resetTimer = useCallback(() => {
        // Clear any existing timers
        if (idleTimeoutRef.current) {
            window.clearTimeout(idleTimeoutRef.current);
        }
        if (countdownIntervalRef.current) {
            window.clearInterval(countdownIntervalRef.current);
        }

        // Reset state
        setIsIdle(false);
        setCountdown(countdownTimeInSeconds);

        // Start the idle timer
        idleTimeoutRef.current = window.setTimeout(() => {
            setIsIdle(true);
        }, effectiveIdleTimeInSeconds * 1000);
    }, [effectiveIdleTimeInSeconds, countdownTimeInSeconds]);

    // This effect runs when the modal becomes visible (isIdle becomes true)
    useEffect(() => {
        if (isIdle) {
            countdownIntervalRef.current = window.setInterval(() => {
                setCountdown(prevCountdown => {
                    if (prevCountdown <= 1) {
                        clearInterval(countdownIntervalRef.current!);
                        onIdle();
                        return 0;
                    }
                    return prevCountdown - 1;
                });
            }, 1000);
        }

        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, [isIdle, onIdle]);

    // This effect sets up and cleans up the global event listeners
    useEffect(() => {
        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

        const handleActivity = () => {
            resetTimer();
        };

        // Initialize timer on mount
        resetTimer();

        // Add event listeners
        events.forEach(event => window.addEventListener(event, handleActivity));

        // Cleanup function
        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
            if (idleTimeoutRef.current) {
                window.clearTimeout(idleTimeoutRef.current);
            }
            if (countdownIntervalRef.current) {
                window.clearInterval(countdownIntervalRef.current);
            }
        };
    }, [resetTimer]);

    return { isIdle, countdown, resetTimer };
};
